/**
 * clinicalTools.js — Реальные клинические инструменты
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Заменяет ВСЕ заглушки на рабочий код.
 * Использует:
 *  - cornerstone cache для доступа к volume scalarData
 *  - aiModule.js для calciumScoring, segmentCardiacChambers, MedSAM
 *  - Собственные алгоритмы: эмфизема, CTR, BMD, пневмоторакс, region grow
 */

import { estimateEF, clearSamCache, hasAlgorithmicFallback, calciumScoring, segmentCardiacChambers, isModelAvailable, samEncodeImage, samDecodePoints, loadModel, runSegmentation3DLoop } from '../ai/aiModule.js';
import { getRenderingEngine, getViewportIds } from '../core/viewport.js';
import { showToast } from '../utils/helpers.js';
import { cornerstone } from '../core/init.js';
// [7.0.37] clinicalWorker.js удалён как мёртвый код (_tryWorker нигде не вызывался).
// Весь клинический счёт идёт на главном потоке: кальций — aiModule.calciumScoring,
// остальное — инлайн в этом файле.
import { log } from '../utils/logger.js';
import { bmdCortex } from './bmdCortexCore.js';   // [7.0.38] BMD по Pickhardt (позвонок по ободку)

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();



// ══════════════════════════════════════════════════════════════
//  HELPERS: доступ к данным объёма
// ══════════════════════════════════════════════════════════════

/**
 * Extract volume scalar data, dimensions, spacing from currently loaded volume.
 * Tries multiple paths: viewport actors → vtk.js mapper → Cornerstone cache.
 * @param {string} [volumeId] - Cornerstone volume ID. If omitted, searches all viewports.
 * @returns {{scalarData: TypedArray, dims: number[], spacing: number[], origin: number[], imageData: Object}|null}
 */
export function getVolumeData(volumeId) {
  const cs = cornerstone; 
  const re = getRenderingEngine();
  let data = null, dims = null, spacing = null, origin = null, imageData = null;

  if (re) {
    try {
      const vpIds = getViewportIds();
      for (const vpId of vpIds) {
        if (data) break;
        const vp = re.getViewport(vpId);
        if (!vp) continue;
        const actors = vp.getActors?.() ||[];
        for (const actorEntry of actors) {
          const actor = actorEntry?.actor;
          const mapper = actor?.getMapper?.();
          const id = mapper?.getInputData?.();
          if (id) {
            const pd = id.getPointData?.();
            const sc = pd?.getScalars?.();
            if (sc && typeof sc.getData === 'function') {
              data = sc.getData();
              dims = id.getDimensions();
              spacing = id.getSpacing();
              origin = id.getOrigin();
              imageData = id;
              log.debug('[ClinicalTools] Data via VTK.js path, dims:', dims);
              break;
            }
          }
        }
      }
    } catch (e) {
      log.warn('[ClinicalTools] VTK.js path failed:', e.message);
    }
  }

  if (!data && volumeId && cs?.cache) {
    try {
      const vid = volumeId.startsWith('cornerstoneStreamingImageVolume:')
        ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`;
      const vol = cs.cache.getVolume?.(vid);
      if (vol) {
        const vm = vol.voxelManager;
        data = vm?.getCompleteScalarDataArray?.() || null;

        if (!data && vol.imageData) {
          const pd = vol.imageData.getPointData?.();
          const sc = pd?.getScalars?.();
          data = sc?.getData?.() || null;
          imageData = vol.imageData;
        }

        if (data) {
          dims = vol.dimensions;
          spacing = vol.spacing;
          origin = vol.origin;
          if (!imageData) imageData = vol.imageData;
          log.debug('[ClinicalTools] Data via voxelManager, dims:', dims);
        }
      }
    } catch (e) {
      log.warn('[ClinicalTools] voxelManager path failed:', e.message);
    }
  }

  if (!data && volumeId && cs?.cache) {
    try {
      const vid = volumeId.startsWith('cornerstoneStreamingImageVolume:')
        ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`;
      const vol = cs.cache.getVolume?.(vid);
      if (vol) {
        data = vol.getScalarData?.();
        if (data) {
          dims = vol.dimensions;
          spacing = vol.spacing;
          origin = vol.origin;
          imageData = vol.imageData;
          log.debug('[ClinicalTools] Data via legacy getScalarData(), dims:', dims);
        }
      }
    } catch (e) {
      log.warn('[ClinicalTools] legacy getScalarData failed (expected in v2):', e.message);
    }
  }

  if (!data || !dims || data.length === 0) {
    // Last resort: try first volume from cache directly
    try {
      const vols = cs?.cache?.getVolumes?.() || [];
      for (const vol of vols) {
        if (!vol) continue;
        let sd = null;
        try { sd = vol.voxelManager?.getCompleteScalarDataArray?.(); } catch(e) {}
        if (!sd) try { const pd = vol.imageData?.getPointData?.()?.getScalars?.(); sd = pd?.getData?.(); } catch(e) {}
        if (!sd) try { sd = vol.getScalarData?.(); } catch(e) {}
        if (!sd) try { sd = vol.scalarData; } catch(e) {}
        if (sd && sd.length > 0) {
          data = sd;
          dims = vol.dimensions || vol.imageData?.getDimensions?.();
          spacing = vol.spacing || vol.imageData?.getSpacing?.();
          origin = vol.origin || vol.imageData?.getOrigin?.();
          imageData = vol.imageData;
          log.debug('[ClinicalTools] Data via cache.getVolumes() fallback, dims:', dims);
          break;
        }
      }
    } catch(e) {
      log.warn('[ClinicalTools] getVolumes fallback failed:', e.message);
    }
  }

  if (!data || !dims || data.length === 0) {
    log.warn('[ClinicalTools] No volume data found. volumeId:', volumeId);
    return null;
  }

  return {
    scalarData: data,
    dims,
    spacing,
    origin,
    imageData,
  };
}

/**
 * Get pixel data for current slice of a viewport.
 * Falls back to 'axial', then first non-3D viewport if viewportId not specified.
 * @param {string} [viewportId] - Target viewport ID. Defaults to 'axial'.
 * @returns {{pixelData: TypedArray, width: number, height: number, sliceIdx: number, dims: number[]}|null}
 */
export function getSlicePixelData(viewportId) {
  const re = getRenderingEngine();
  if (!re) return null;

  const allIds = getViewportIds();
  const vpId = viewportId
    || allIds.find(id => id === 'axial')
    || allIds.find(id => id !== 'volume3d' && id !== 'view-3d')
    || allIds[0];
  const vp = re.getViewport(vpId);
  if (!vp) return null;

  try {
    const imgData = vp.getImageData?.();
    if (imgData) {
      const dims = imgData.dimensions;
      const sliceIdx = vp.getCurrentImageIdIndex?.() || 0;
      const w = dims[0], h = dims[1];
      const sliceSize = w * h;

      let sd = null;
      try { sd = imgData.scalarData; } catch (e) { log.debug('[ClinicalTools]', e.message); }

      if (!sd && imgData.getPointData) {
        const sc = imgData.getPointData()?.getScalars?.();
        sd = sc?.getData?.();
      }

      if (sd && sd.length > 0) {
        let pixelData;
        if (dims.length >= 3 && dims[2] > 1) {
          const start = sliceIdx * sliceSize;
          pixelData = sd.slice(start, start + sliceSize);
        } else {
          pixelData = sd;
        }
        return { pixelData, width: w, height: h, sliceIdx, dims };
      }
    }

    const actors = vp.getActors?.() ||[];
    for (const actorEntry of actors) {
      const mapper = actorEntry?.actor?.getMapper?.();
      const id = mapper?.getInputData?.();
      if (id) {
        const sc = id.getPointData?.()?.getScalars?.();
        const sd = sc?.getData?.();
        if (sd && sd.length > 0) {
          const dims = id.getDimensions();
          const w = dims[0], h = dims[1];
          const sliceIdx = vp.getCurrentImageIdIndex?.() || 0;
          const sliceSize = w * h;
          let pixelData;
          if (dims[2] > 1) {
            const start = sliceIdx * sliceSize;
            pixelData = sd.slice(start, start + sliceSize);
          } else {
            pixelData = sd;
          }
          return { pixelData, width: w, height: h, sliceIdx, dims };
        }
      }
    }

    return null;
  } catch (e) {
    log.warn('[Clinical] getSlicePixelData error:', e);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════
//  РЕЗУЛЬТАТЫ — модальное окно
// ══════════════════════════════════════════════════════════════

export function showResultModal(title, bodyHtml, options = {}) {
  document.getElementById('clinical-result-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'clinical-result-modal';
  modal.style.cssText = `
    position:fixed; inset:0; z-index:10000; display:flex; align-items:center; justify-content:center;
    background:rgba(0,0,0,0.7); backdrop-filter:blur(4px);
  `;
  modal.innerHTML = `
    <div style="background:#1a1f2e; border:1px solid #334155; border-radius:12px; max-width:600px; width:90%;
                max-height:80vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,0.5);">
      <div style="display:flex; justify-content:space-between; align-items:center; padding:16px 20px;
                  border-bottom:1px solid #334155;">
        <h3 style="margin:0; color:#fff; font-size:1.1rem;">${title}</h3>
        <button id="clinical-modal-close" style="background:none; border:none; color:#888; font-size:1.4rem;
                cursor:pointer; padding:0 4px;">&times;</button>
      </div>
      <div style="padding:20px; color:#c5c6c7; font-size:0.9rem; line-height:1.7;">
        ${bodyHtml}
      </div>
      ${options.footer ? `<div style="padding:12px 20px; border-top:1px solid #334155; text-align:right;">${options.footer}</div>` : ''}
    </div>
  `;
  document.body.appendChild(modal);

  modal.querySelector('#clinical-modal-close').onclick = () => modal.remove();
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); }, { signal: _ac.signal });
}

// Стили для таблиц результатов
const TABLE_STYLE = `
  width:100%; border-collapse:collapse; margin:10px 0;
`;
const TH_STYLE = `
  text-align:left; padding:8px 12px; background:rgba(255,255,255,0.05);
  color:#66fcf1; font-weight:600; border-bottom:1px solid #334155;
`;
const TD_STYLE = `
  padding:8px 12px; border-bottom:1px solid rgba(255,255,255,0.05);
`;

function riskColor(level) {
  return['#22c55e','#84cc16','#eab308','#f97316','#ef4444'][level] || '#888';
}


// ══════════════════════════════════════════════════════════════
//  1. AGATSTON CALCIUM SCORING
// ══════════════════════════════════════════════════════════════

export async function runAgatstonScore(volumeId) {
  try {
    showToast('💎 Agatston: анализ кальция...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём (MPR)', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
  
    let maxHU = -1024;
    for (let i = 0; i < Math.min(scalarData.length, 1000000); i++) {
      if (scalarData[i] > maxHU) maxHU = scalarData[i];
    }
    if (maxHU < 130) {
      showToast('⚠️ Нет вокселей ≥130 HU — кальция не обнаружено', 'success');
      showResultModal('💎 Agatston Score', `
        <div style="text-align:center; padding:20px;">
          <div style="font-size:3rem; color:#22c55e; margin-bottom:10px;">0</div>
          <div style="font-size:1.1rem; color:#22c55e;">Кальций не обнаружен</div>
          <div style="margin-top:15px; color:#888;">Риск: Минимальный</div>
        </div>
      `);
      return;
    }
  
    const result = await calciumScoring(scalarData, nx, ny, nz, spacing);
  
    const scoreColor = riskColor(result.riskLevel);
    let lesionRows = '';
    for (const l of result.lesions.slice(0, 10)) {
      lesionRows += `<tr>
        <td style="${TD_STYLE}">${l.id}</td>
        <td style="${TD_STYLE}">${l.voxels}</td>
        <td style="${TD_STYLE}">${l.maxHU}</td>
        <td style="${TD_STYLE}">${l.meanHU}</td>
        <td style="${TD_STYLE}">${l.volumeMm3.toFixed(1)} мм³</td>
      </tr>`;
    }
  
    showResultModal('💎 Agatston Calcium Score', `
      <div style="text-align:center; margin-bottom:20px;">
        <div style="font-size:3rem; font-weight:700; color:${scoreColor};">${result.totalScore}</div>
        <div style="font-size:1.1rem; color:${scoreColor}; margin-top:5px;">${result.risk}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr>
          <th style="${TH_STYLE}">Параметр</th>
          <th style="${TH_STYLE}">Значение</th>
        </tr>
        <tr><td style="${TD_STYLE}">Agatston Score</td><td style="${TD_STYLE}"><strong>${result.totalScore}</strong></td></tr>
        <tr><td style="${TD_STYLE}">Объём кальция</td><td style="${TD_STYLE}">${result.volumeMm3} мм³</td></tr>
        <tr><td style="${TD_STYLE}">Mass Score</td><td style="${TD_STYLE}">${result.massScore}</td></tr>
        <tr><td style="${TD_STYLE}">Кол-во очагов</td><td style="${TD_STYLE}">${result.lesionCount}</td></tr>
        <tr><td style="${TD_STYLE}">Категория риска</td><td style="${TD_STYLE}; color:${scoreColor}; font-weight:600;">${result.risk} (${result.riskLevel}/4)</td></tr>
      </table>
      ${result.lesions.length > 0 ? `
        <h4 style="color:#fff; margin:15px 0 8px;">Очаги кальцификации (топ-10):</h4>
        <table style="${TABLE_STYLE}">
          <tr>
            <th style="${TH_STYLE}">#</th>
            <th style="${TH_STYLE}">Воксели</th>
            <th style="${TH_STYLE}">Max HU</th>
            <th style="${TH_STYLE}">Mean HU</th>
            <th style="${TH_STYLE}">Объём</th>
          </tr>
          ${lesionRows}
        </table>
      ` : ''}
      <div style="margin-top:15px; padding:10px; background:rgba(255,255,255,0.03); border-radius:6px; font-size:0.8rem; color:#888;">
        Классификация по ACC/AHA 2010: 0=минимальный, 1-10=очень низкий, 11-100=умеренный, 101-400=высокий, >400=очень высокий.<br>
        Данные объёма: ${nx}×${ny}×${nz}, spacing: ${spacing.map(s=>s.toFixed(2)).join('×')} мм.
      </div>
    `);
  
    showToast(`💎 Agatston: ${result.totalScore} — ${result.risk}`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runAgatstonScore failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  2. ЭМФИЗЕМА — количественный анализ
// ══════════════════════════════════════════════════════════════

export async function runEmphysemaQuantification(volumeId) {
  try {
    showToast('🫁 Эмфизема: подсчёт...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const[nx, ny, nz] = dims;
    const voxelVolMm3 = spacing[0] * spacing[1] * spacing[2];
  
    let lungVoxels = 0, emphVoxels = 0;
    let totalLungHU = 0;
    const emphThreshold = -910; 
  
    for (let i = 0; i < scalarData.length; i++) {
      const hu = scalarData[i];
      if (hu >= -1000 && hu <= -200) {
        lungVoxels++;
        totalLungHU += hu;
        if (hu < emphThreshold) {
          emphVoxels++;
        }
      }
    }
  
    if (lungVoxels < 1000) {
      showToast('⚠️ Лёгочная ткань не найдена (мало вокселей в диапазоне -1000...-200)', 'warning');
      return;
    }
  
    const emphPercent = (emphVoxels / lungVoxels * 100).toFixed(1);
    const lungVolMl = (lungVoxels * voxelVolMm3 / 1000).toFixed(0);
    const emphVolMl = (emphVoxels * voxelVolMm3 / 1000).toFixed(0);
    const meanLungHU = (totalLungHU / lungVoxels).toFixed(1);
  
    let laa950 = 0;
    for (let i = 0; i < scalarData.length; i++) {
      if (scalarData[i] >= -1000 && scalarData[i] <= -200 && scalarData[i] < -950) laa950++;
    }
    const laa950Pct = (laa950 / lungVoxels * 100).toFixed(1);
  
    const lungHUs =[];
    for (let i = 0; i < scalarData.length; i++) {
      if (scalarData[i] >= -1000 && scalarData[i] <= -200) lungHUs.push(scalarData[i]);
    }
    lungHUs.sort((a, b) => a - b);
    const p15 = lungHUs[Math.floor(lungHUs.length * 0.15)] || -1000;
  
    let severity, sevColor;
    if (emphPercent < 5)       { severity = 'Норма / минимальная'; sevColor = '#22c55e'; }
    else if (emphPercent < 15) { severity = 'Лёгкая'; sevColor = '#eab308'; }
    else if (emphPercent < 25) { severity = 'Умеренная'; sevColor = '#f97316'; }
    else                       { severity = 'Тяжёлая'; sevColor = '#ef4444'; }
  
    showResultModal('🫁 Количественная оценка эмфиземы', `
      <div style="text-align:center; margin-bottom:20px;">
        <div style="font-size:2.5rem; font-weight:700; color:${sevColor};">${emphPercent}%</div>
        <div style="color:${sevColor};">${severity}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">LAA -910 (Müller)</td><td style="${TD_STYLE}"><strong>${emphPercent}%</strong> (${emphVolMl} мл)</td></tr>
        <tr><td style="${TD_STYLE}">LAA -950 (тяжёлая)</td><td style="${TD_STYLE}">${laa950Pct}%</td></tr>
        <tr><td style="${TD_STYLE}">P15 (перцентиль)</td><td style="${TD_STYLE}">${p15} HU</td></tr>
        <tr><td style="${TD_STYLE}">Объём лёгких</td><td style="${TD_STYLE}">${lungVolMl} мл</td></tr>
        <tr><td style="${TD_STYLE}">Средняя плотность</td><td style="${TD_STYLE}">${meanLungHU} HU</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей лёгких</td><td style="${TD_STYLE}">${lungVoxels.toLocaleString()}</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей эмфиземы</td><td style="${TD_STYLE}">${emphVoxels.toLocaleString()}</td></tr>
      </table>
      <div style="margin-top:12px; padding:10px; background:rgba(255,255,255,0.03); border-radius:6px; font-size:0.8rem; color:#888;">
        LAA (Low Attenuation Area): процент вокселей лёгких ниже порога.<br>
        Порог -910 HU (Müller, 1988) — стандартный. -950 HU — тяжёлая эмфизема.<br>
        P15 — 15-й перцентиль плотности (Dirksen 1999).
      </div>
    `);
  
    showToast(`🫁 Эмфизема: ${emphPercent}% — ${severity}`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runEmphysemaQuantification failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  3. CTR — Кардиоторакальный индекс
// ══════════════════════════════════════════════════════════════

export async function runCTR(volumeId) {
  try {
    showToast('❤️ КТИ: анализ...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
  
    const sliceSize = nx * ny;
    const zStart = Math.floor(nz * 0.3);
    const zEnd = Math.floor(nz * 0.6);
    let bestZ = Math.floor(nz * 0.45), bestHeartDiam = 0, bestThoraxDiam = 0;
    let bestHeartLeft = 0, bestHeartRight = 0, bestBodyLeft = 0, bestBodyRight = 0;
  
    for (let z = zStart; z < zEnd; z++) {
      const off = z * sliceSize;
      let bL = nx, bR = 0, hL = nx, hR = 0;
      for (let y = Math.floor(ny * 0.3); y < Math.floor(ny * 0.8); y++) {
        for (let x = 0; x < nx; x++) {
          const hu = scalarData[off + y * nx + x];
          if (hu > -500) { if (x < bL) bL = x; if (x > bR) bR = x; }
          if (hu > 0 && hu < 500) { if (x < hL) hL = x; if (x > hR) hR = x; }
        }
      }
      const hDiam = (hR - hL) * spacing[0];
      if (hDiam > bestHeartDiam) {
        bestHeartDiam = hDiam;
        bestThoraxDiam = (bR - bL) * spacing[0];
        bestZ = z; bestHeartLeft = hL; bestHeartRight = hR; bestBodyLeft = bL; bestBodyRight = bR;
      }
    }
  
    const thoraxDiamMm = bestThoraxDiam;
    const heartDiamMm = bestHeartDiam;
  
    if (thoraxDiamMm < 100 || heartDiamMm < 30) {
      showToast('⚠️ Не удалось определить контуры грудной клетки', 'warning');
      return;
    }
  
    const ctr = (heartDiamMm / thoraxDiamMm).toFixed(3);
    const ctrPct = (ctr * 100).toFixed(1);
    const isNormal = ctr < 0.50;
  
    showResultModal('❤️ Кардиоторакальный индекс (CTR)', `
      <div style="text-align:center; margin-bottom:20px;">
        <div style="font-size:3rem; font-weight:700; color:${isNormal ? '#22c55e' : '#ef4444'};">${ctrPct}%</div>
        <div style="color:${isNormal ? '#22c55e' : '#ef4444'};">${isNormal ? 'Норма (<50%)' : 'Кардиомегалия (≥50%)'}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">CTR</td><td style="${TD_STYLE}"><strong>${ctr}</strong></td></tr>
        <tr><td style="${TD_STYLE}">Поперечник сердца</td><td style="${TD_STYLE}">${heartDiamMm.toFixed(1)} мм</td></tr>
        <tr><td style="${TD_STYLE}">Поперечник грудной клетки</td><td style="${TD_STYLE}">${thoraxDiamMm.toFixed(1)} мм</td></tr>
        <tr><td style="${TD_STYLE}">Срез анализа</td><td style="${TD_STYLE}">#${bestZ + 1} / ${nz} (макс. поперечник)</td></tr>
      </table>
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        CTR >0.50 = кардиомегалия (Danzer, 1919). Автоматический поиск среза с максимальным поперечником сердца (${zEnd - zStart} срезов проанализировано).
      </div>
    `);
  
    showToast(`❤️ КТИ: ${ctrPct}% — ${isNormal ? 'норма' : 'кардиомегалия'}`, isNormal ? 'success' : 'warning');
  } catch (e) { log.warn(`[clinicalTools] runCTR failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  4. BMD — Костная минеральная плотность
// ══════════════════════════════════════════════════════════════

export async function runBMD(volumeId) {
  try {
    showToast('💪 BMD: анализ позвонков...', 'info');

    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }

    // [7.0.38] Позвонок ищется по кортикальному ободку, дальше средний HU губчатой,
    // пороги Pickhardt 160/110. До этого стоял фиксированный квадратик в середине
    // кадра на высоте 0.7·H, и всё в окне 50–400 HU считалось костью. Он не знал,
    // что под ним: на однородных тканях 60 HU выдавал «Остеопороз», а у пациента на
    // животе (КТ-колонография — ровно та популяция скрининга) попадал на контрастную
    // аорту ~300 HU и писал «Норма» при позвонке 90 HU. Ободок в таких случаях
    // честно отказывается — см. tests/bmd_test.mjs, разделы 6 и 7.
    const r = bmdCortex(vd.scalarData, vd.dims);

    if (r.trabecularHU === null) {
      showToast('⚠️ Тело позвонка не локализовано', 'warning');
      showResultModal('💪 Костная минеральная плотность (BMD)', `
        <div style="text-align:center; margin-bottom:16px; color:#888; font-weight:600;">${r.category}</div>
        <div style="font-size:0.85rem; color:#888;">${r.note}</div>
      `);
      return;
    }

    const hu = r.trabecularHU;
    showResultModal('💪 Костная минеральная плотность (BMD)', `
      <div style="text-align:center; margin-bottom:20px;">
        <div style="font-size:2.5rem; font-weight:700; color:${r.catColor};">${hu.toFixed(0)} HU</div>
        <div style="color:${r.catColor};">${r.category}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Средний HU губчатой (L-позвонки)</td><td style="${TD_STYLE}"><strong>${hu.toFixed(1)} HU</strong></td></tr>
        <tr><td style="${TD_STYLE}">Диапазон по срезам</td><td style="${TD_STYLE}">${r.minHU.toFixed(0)} — ${r.maxHU.toFixed(0)} HU</td></tr>
        <tr><td style="${TD_STYLE}">Срезов с позвонком</td><td style="${TD_STYLE}">${r.slices}</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей губчатой</td><td style="${TD_STYLE}">${r.trabVoxels.toLocaleString()}</td></tr>
      </table>
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Оппортунистический скрининг BMD по КТ (Pickhardt 2013): тело позвонка по кортикальному ободку,
        средний HU губчатой кости.<br>
        &gt;160 HU = норма, 110–160 = остеопения (погранично), &lt;110 = подозрение на остеопороз.<br>
        Только нативная фаза. Авто-ROI приблизительный — подтвердить DXA/QCT.
      </div>
    `);

    showToast(`💪 BMD: ${hu.toFixed(0)} HU — ${r.category}`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runBMD failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  5. ПНЕВМОТОРАКС — асимметрия воздуха
// ══════════════════════════════════════════════════════════════

export async function runPneumothoraxDetection(volumeId) {
  try {
    showToast('⚠️ Пневмоторакс: анализ...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
    const midX = Math.floor(nx / 2);
    const sliceSize = nx * ny;
  
    let leftAir = 0, rightAir = 0, leftTotal = 0, rightTotal = 0;
  
    for (let z = Math.floor(nz * 0.1); z < Math.floor(nz * 0.9); z++) {
      for (let y = Math.floor(ny * 0.1); y < Math.floor(ny * 0.8); y++) {
        for (let x = Math.floor(nx * 0.1); x < Math.floor(nx * 0.9); x++) {
          const hu = scalarData[z * sliceSize + y * nx + x];
          if (hu < -200) { 
            if (x < midX) {
              leftTotal++;
              if (hu < -900) leftAir++;
            } else {
              rightTotal++;
              if (hu < -900) rightAir++;
            }
          }
        }
      }
    }
  
    const leftPct = leftTotal > 0 ? (leftAir / leftTotal * 100) : 0;
    const rightPct = rightTotal > 0 ? (rightAir / rightTotal * 100) : 0;
    const asymmetry = Math.abs(leftPct - rightPct);
    const side = leftPct > rightPct ? 'Левая' : 'Правая';
  
    let verdict, verdColor;
    if (asymmetry < 3) { verdict = 'Нет признаков пневмоторакса'; verdColor = '#22c55e'; }
    else if (asymmetry < 10) { verdict = `Возможный пневмоторакс (${side})—необходима верификация`; verdColor = '#eab308'; }
    else { verdict = `Вероятный пневмоторакс (${side} сторона)`; verdColor = '#ef4444'; }
  
    showResultModal('⚠️ Детекция пневмоторакса', `
      <div style="text-align:center; margin-bottom:20px; color:${verdColor}; font-weight:600;">${verdict}</div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Воздух слева</td><td style="${TD_STYLE}">${leftPct.toFixed(1)}% (${leftAir.toLocaleString()} вокселей)</td></tr>
        <tr><td style="${TD_STYLE}">Воздух справа</td><td style="${TD_STYLE}">${rightPct.toFixed(1)}% (${rightAir.toLocaleString()} вокселей)</td></tr>
        <tr><td style="${TD_STYLE}">Асимметрия</td><td style="${TD_STYLE}; color:${verdColor}; font-weight:600;">${asymmetry.toFixed(1)}%</td></tr>
      </table>
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Анализ асимметрии воздуха (<-900 HU) между правым и левым лёгкими.<br>
        Не заменяет визуальную оценку рентгенолога.
      </div>
    `);
  } catch (e) { log.warn(`[clinicalTools] runPneumothoraxDetection failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  6. КАМЕРЫ СЕРДЦА + EF
// ══════════════════════════════════════════════════════════════

export async function runCardiacSegmentation(volumeId) {
  try {
    showToast('❤️ Сегментация камер сердца...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
  
    const result = await segmentCardiacChambers(scalarData, nx, ny, nz, spacing);
    if (!result) { showToast('❌ Сегментация не удалась', 'error', 2000); return; }
  
    const ch = result.chambers;
    let chambersHtml = '';
    const chamberNames = { LV: 'Левый желудочек', RV: 'Правый желудочек', LA: 'Левое предсердие', RA: 'Правое предсердие' };
    const chamberColors = { LV: '#ef4444', RV: '#3b82f6', LA: '#f97316', RA: '#22c55e' };
  
    for (const [key, name] of Object.entries(chamberNames)) {
      const c = ch[key];
      if (c) {
        chambersHtml += `
          <tr>
            <td style="${TD_STYLE}"><span style="color:${chamberColors[key]};">●</span> ${name}</td>
            <td style="${TD_STYLE}">${c.volumeMl} мл</td>
            <td style="${TD_STYLE}">${c.voxels.toLocaleString()}</td>
          </tr>
        `;
      }
    }
  
    let efHtml = '';
    if (ch.LV) {
      const ef = estimateEF(ch.LV.volumeMl);
      efHtml = ef.ef !== null
        ? `<div style="margin-top:15px; text-align:center;">
             <div style="font-size:1.5rem; color:#66fcf1;">EF ≈ ${ef.ef}%</div>
             <div style="font-size:0.8rem; color:#888;">Метод: ${ef.method}</div>
           </div>`
        : `<div style="margin-top:15px; color:#888; text-align:center; font-size:0.85rem;">
             EF: требуется двухфазное сканирование (ED + ES)
           </div>`;
    }
  
    showResultModal('❤️ Сегментация камер сердца', `
      <div style="text-align:center; margin-bottom:15px;">
        <span style="font-size:0.85rem; color:#888;">Контраст: ${result.hasContrast ? '✅ Да' : '❌ Нет'}</span>
      </div>
      ${chambersHtml ? `
        <table style="${TABLE_STYLE}">
          <tr>
            <th style="${TH_STYLE}">Камера</th>
            <th style="${TH_STYLE}">Объём</th>
            <th style="${TH_STYLE}">Вокселей</th>
          </tr>
          ${chambersHtml}
        </table>
      ` : '<div style="text-align:center; color:#888; padding:20px;">Камеры не обнаружены. Для точной сегментации нужна КТ с контрастированием.</div>'}
      ${efHtml}
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Пороговая сегментация: кровь ${result.hasContrast ? '150-500' : '25-90'} HU + морфология.<br>
        Для клинической оценки EF используйте двухфазный КТ-протокол.
      </div>
    `);
  } catch (e) { log.warn(`[clinicalTools] runCardiacSegmentation failed:`, e.message); showToast('❌ Камеры: ' + e.message, 'error', 3000); }
}


// ══════════════════════════════════════════════════════════════
//  7. REGION GROW 3D — Заливка от точки
// ══════════════════════════════════════════════════════════════

/**
 * [11.1.38] Семя для роста области — центр тяжести нарисованного сегмента.
 * Врач ставит кистью точку внутри органа; это его «ткнуть мышью», только
 * работающее в любом срезовом окне и переживающее смену раскладки.
 *
 * Почему центр тяжести, а не первый воксель: врач чаще обводит КОНТУР, и
 * первый воксель кольца лежит на границе органа — рост оттуда сразу
 * утечёт в соседа. Центр тяжести кольца лежит внутри органа.
 *
 * @returns {Promise<number|null>} линейный индекс вокселя или null, если метки нет
 */
async function _семяИзМаски(dims, индекс = 1) {
  try {
    const { томМаски } = await import('../core/organView.js');
    const м = томМаски();
    if (!м || м.ошибка || !м.пиксели?.length) return null;
    const [nx, ny] = dims;
    let сумма = 0, сx = 0, сy = 0, сz = 0;
    for (let i = 0; i < м.пиксели.length; i++) {
      if (м.пиксели[i] !== индекс) continue;
      const z = Math.floor(i / (nx * ny));
      const y = Math.floor((i % (nx * ny)) / nx);
      const x = i % nx;
      сx += x; сy += y; сz += z; сумма++;
    }
    if (!сумма) return null;
    const x = Math.round(сx / сумма), y = Math.round(сy / сумма), z = Math.round(сz / сумма);
    const idx = z * nx * ny + y * nx + x;
    return (idx >= 0 && idx < м.пиксели.length) ? idx : null;
  } catch (e) { return null; }
}

export async function runRegionGrow3D(volumeId, options = {}) {
  try {
    // [11.1.38.1] ТОМ БЕРЁТСЯ ПОД РАЗМЕР МАСКИ, а не «первый попавшийся».
    //
    // Владелец поймал на живом: «Seed HU undefined», «Диапазон NaN — NaN»,
    // «В маску не записано: размер маски не совпал с объёмом». Причина:
    // getVolumeData идёт через ПЕРВОГО актёра окна, а с появлением
    // сегментации первым актёром в срезовом окне вполне может оказаться
    // САМА МАСКА (labelmap-наложение) или иной том другой сетки. Семя тогда
    // считается по одним размерам, плотность читается из других — отсюда
    // undefined и NaN, а результат некуда записать.
    //
    // Теперь: сначала маска (она — адресат результата), затем ищем том с
    // ТОЙ ЖЕ длиной массива, начиная с поимённого volumeId и далее по кешу,
    // пропуская сами маски. Совпадение длины — единственная надёжная
    // сверка сеток, доступная без метаданных.
    const { томМаски: _томМаски } = await import('../core/organView.js');
    const маскаДляРоста = _томМаски();
    let vd = getVolumeData(volumeId);
    const нужнаДлина = маскаДляРоста?.пиксели?.length || 0;
    if (нужнаДлина && vd?.scalarData?.length !== нужнаДлина) {
      const кэш = cornerstone?.cache;
      const кандидаты = [];
      try {
        const прям = кэш?.getVolume?.(String(volumeId).startsWith('cornerstoneStreamingImageVolume:')
          ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`);
        if (прям) кандидаты.push(прям);
        for (const v of (кэш?.getVolumes?.() || [])) {
          if (!/labelmap|mikhmel-seg/i.test(String(v?.volumeId || ''))) кандидаты.push(v);
        }
      } catch (eк) { /* кеш недоступен — останемся на vd */ }
      for (const v of кандидаты) {
        const д = v?.voxelManager?.getCompleteScalarDataArray?.() || v?.getScalarData?.();
        if (д?.length === нужнаДлина) {
          vd = { scalarData: д, dims: v.dimensions, spacing: v.spacing || [1, 1, 1], origin: v.origin };
          log.info(`[RegionGrow] том подобран под размер маски: ${v.volumeId}`);
          break;
        }
      }
    }
    if (!vd?.scalarData?.length) { showToast('⚠️ Загрузите объём', 'warning'); return; }

    const { scalarData, dims, spacing } = vd;
    const[nx, ny, nz] = dims;
    const range = options.range || 100; 

    // [11.1.38] СЕМЯ БЕРЁТСЯ ИЗ МАСКИ ВРАЧА, А НЕ ИЗ ЦЕНТРА ОБЪЁМА.
    //
    // Здесь стояло seedX/Y/Z = середина объёма — жёстко, без единого вопроса
    // к человеку. Владелец справедливо написал: «она даже ткнуть никуда не
    // даёт, сразу выводит модальное окно». Центр КТ живота — это просвет
    // кишки или позвоночник, а не орган; отсюда и его отчёт «Seed HU 14,
    // Seed координаты [256, 256, 0], 0.0 мл» — программа растила пузырь
    // мягких тканей от случайной точки и честно сообщала ерунду. Ещё и
    // seedZ вышел 0: dims приходили от ПЕРВОГО актёра первого окна, а им
    // мог оказаться не том исследования.
    //
    // Теперь: том берётся ПОИМЁННО по volumeId, а семя — центр тяжести уже
    // нарисованного сегмента. То есть врач ставит кистью точку (или мазок)
    // внутри органа — это и есть его «ткнуть», причём в любом из срезовых
    // окон. Нет мазка — честный отказ словами, а не выдуманная точка.
    const семя = options.seedIndex ?? await _семяИзМаски(dims, options.segIndex || 1);
    if (семя != null && (семя < 0 || семя >= scalarData.length)) {
      showToast('Метка кистью не ложится на сетку объёма — сообщите об этом: сетки маски и снимка разошлись.', 'error', 6000);
      return;
    }
    if (семя == null) {
      showToast('Ткните кистью внутрь органа (одной точки хватит) и нажмите снова: рост области идёт ОТ вашей метки, выдумывать её за вас я не буду.', 'warning', 7000);
      return;
    }
    const seedIdx = семя;
    const seedZ = Math.floor(seedIdx / (nx * ny));
    const seedY = Math.floor((seedIdx % (nx * ny)) / nx);
    const seedX = seedIdx % nx;
    const seedHU = scalarData[seedIdx];
  
    showToast(`📦 Наращивание области: затравка=${seedHU} HU, диапазон ±${range}...`, 'info');
  
    const low = seedHU - range;
    const high = seedHU + range;
    const visited = new Uint8Array(scalarData.length);
    const queue =[seedIdx];
    visited[seedIdx] = 1;
    let count = 0;
    const MAX_VOXELS = 5_000_000; 

    // [11.1.43] РОСТ ЗАПЕРТ В САМОЙ РАЗМЕТКЕ, РАЗДУТОЙ НА ЗАПАС.
    //
    // Прямоугольная рамка (11.1.41) была слишком щедрой: владелец обводил
    // селезёнку в трёх проекциях, ящик растянулся на 150×242×150 вокселей, и
    // внутри него оказались печень и мышцы спины — рост заполнил 70% ящика
    // (1 617 мл, скриншот). Ящик вокруг органа не есть орган.
    //
    // Теперь разрешённая зона повторяет ФОРМУ разметки: расти можно только
    // туда, куда можно дойти от обводок за 10 мм. Печень за пределами органа
    // недостижима, а добрать неразмеченную середину — можно.
    let зона = null;
    try {
      const { зонаРоста } = await import('../core/maskMorph.js');
      const мм = 10;
      const шагов = Math.max(2, Math.round(мм / Math.min(spacing[0] || 1, spacing[1] || 1, spacing[2] || 1)));
      зона = зонаРоста(маскаДляРоста?.пиксели, dims, options.segIndex || 1, шагов);
      if (зона) {
        let разрешено = 0;
        for (let i = 0; i < зона.length; i++) if (зона[i]) разрешено++;
        log.info(`[RegionGrow] зона роста = разметка + ${мм} мм (${шагов} шагов): ${разрешено.toLocaleString('ru-RU')} вокселей разрешено из ${зона.length.toLocaleString('ru-RU')}`);
      } else {
        log.warn('[RegionGrow] зоны нет: сегмент пуст или маска не найдена — рост пойдёт по всему объёму');
      }
    } catch (eз) { log.warn('[RegionGrow] зона недоступна:', eз.message); }
    const вРамке = зона ? (i) => зона[i] === 1 : () => true;
    const рамка = зона;   // для отчёта: зона есть — значит рост ограничен
  
    while (queue.length > 0 && count < MAX_VOXELS) {
      const ci = queue.shift();
      count++;
      const cz = Math.floor(ci / (nx * ny));
      const rem = ci % (nx * ny);
      const cy = Math.floor(rem / nx);
      const cx = rem % nx;
  
      const neighbors =[];
      if (cx > 0)    neighbors.push(ci - 1);
      if (cx < nx-1) neighbors.push(ci + 1);
      if (cy > 0)    neighbors.push(ci - nx);
      if (cy < ny-1) neighbors.push(ci + nx);
      if (cz > 0)    neighbors.push(ci - nx*ny);
      if (cz < nz-1) neighbors.push(ci + nx*ny);
  
      for (const ni of neighbors) {
        if (!visited[ni] && вРамке(ni)) {
          const hu = scalarData[ni];
          if (hu >= low && hu <= high) {
            visited[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }
  
    const volMm3 = count * spacing[0] * spacing[1] * spacing[2];
    const volMl = (volMm3 / 1000).toFixed(1);

    // [11.1.38] РЕЗУЛЬТАТ УХОДИТ В МАСКУ, а не только в табличку. Раньше
    // выросшая область жила одним числом в модальном окне: ни «Только орган
    // в 3D», ни STL, ни экспорт NIfTI её не видели — расчёт был сам по себе.
    let вМаску = '';
    try {
      const { томМаски, записатьМаску, запомнитьПравкуМаски } = await import('../core/organView.js');
      const м = томМаски();
      // [11.1.40] ТРИ ЗАПРЕТА, купленные бедой владельца: рост с диапазоном
      // ±100 протёк по всему животу (5 млн вокселей, обрезано лимитом) и
      // МОЛЧА влился в сегмент, где лежала ручная работа на 617 мл. Отделить
      // утечку от обводок было нечем.
      //
      //  1. Результат, обрезанный ЛИМИТОМ, не пишется вовсе: рост не
      //     остановился сам, значит область заведомо утекла, и её число
      //     бессмысленно. Пишем словами, что делать.
      //  2. В ЗАНЯТЫЙ сегмент не дописываем: берём первый свободный из 1…5
      //     и говорим об этом. Чужую работу смешивать нельзя.
      //  3. Перед записью запоминается ДЕЛЬТА для отмены — кнопка
      //     «↶ Вернуть маску» в панели «Сегментация».
      const занят = (инд) => { for (let i = 0; i < м.пиксели.length; i++) if (м.пиксели[i] === инд) return true; return false; };
      if (count >= MAX_VOXELS) {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}"><b>НЕ записано: рост упёрся в лимит</b> — область не остановилась сама, значит перетекла в соседние ткани. Число объёма недостоверно. Сузьте диапазон (для контрастной селезёнки ±40…±60) и повторите.</td></tr>`;
      } else if (!м.ошибка && м.пиксели?.length === scalarData.length) {
        const желаемый = options.segIndex || 1;
        let индекс = желаемый;
        let пересел = '';
        if (занят(желаемый)) {
          индекс = 0;
          for (let к = 1; к <= 5; к++) { if (!занят(к)) { индекс = к; break; } }
          if (!индекс) {
            вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">НЕ записано: сегмент ${желаемый} уже занят вашей работой, а свободных сегментов (1…5) нет. Освободите один — смешивать чужую разметку с ростом области я не буду.</td></tr>`;
            индекс = -1;
          } else {
            пересел = ` (сегмент ${желаемый} занят вашей разметкой — она не тронута)`;
          }
        }
        if (индекс > 0) {
          const изменённые = [];
          for (let i = 0; i < visited.length; i++) {
            if (visited[i] && м.пиксели[i] !== индекс) изменённые.push(i);
          }
          запомнитьПравкуМаски(изменённые, м.пиксели, 'рост области');
          for (const i of изменённые) м.пиксели[i] = индекс;
          const зап = записатьМаску(м.пиксели);
          вМаску = зап.ок
            ? `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">записано ${изменённые.length.toLocaleString('ru-RU')} вокселей в сегмент ${индекс}${пересел} — доступно «Только орган в 3D», STL и NIfTI. Не то выросло — «↶ Вернуть маску» в панели «Сегментация».</td></tr>`
            : `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${зап.причина}</td></tr>`;
        }
      } else if (м.ошибка) {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${м.ошибка}</td></tr>`;
      } else {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: размер маски не совпал с объёмом</td></tr>`;
      }
    } catch (e) {
      вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${e.message}</td></tr>`;
    }

    showResultModal('📦 Region Grow 3D', `
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Seed HU</td><td style="${TD_STYLE}">${seedHU}</td></tr>
        <tr><td style="${TD_STYLE}">Диапазон</td><td style="${TD_STYLE}">${low} — ${high} HU</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей найдено</td><td style="${TD_STYLE}">${count.toLocaleString()}${count >= MAX_VOXELS ? ' (лимит!)' : ''}</td></tr>
        <tr><td style="${TD_STYLE}">Объём</td><td style="${TD_STYLE}"><strong>${volMl} мл</strong> (${volMm3.toFixed(0)} мм³)</td></tr>
        <tr><td style="${TD_STYLE}">Seed координаты</td><td style="${TD_STYLE}">[${seedX}, ${seedY}, ${seedZ}] — из вашей метки кистью</td></tr>
        ${вМаску}
      </table>
      <div style="margin-top:8px;color:#8fa3c0;font-size:12px;">${рамка
        ? 'Рост разрешён ТОЛЬКО в 10 мм от ваших обводок — форма зоны повторяет орган, поэтому печень и мышцы за его пределами недостижимы. Внутри зоны рост всё равно идёт по плотности: прилежащая вплотную ткань той же плотности может попасть. Лечится обводками и кнопкой «↶ Вернуть маску».'
        : 'ВНИМАНИЕ: разметки нет — зоны нет, рост идёт по всему объёму и почти наверняка перетечёт в соседние ткани. Обведите орган кистью хотя бы на нескольких срезах.'}</div>
    `);
  
    showToast(`📦 Наращивание области: ${count.toLocaleString()} вокселей, ${volMl} мл`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runRegionGrow3D failed:`, e.message); showToast('❌ Наращивание области: ' + e.message, 'error', 3000); }
}


// ══════════════════════════════════════════════════════════════
//  8. FRACTURE DETECTION — 3D градиент на костях
// ══════════════════════════════════════════════════════════════

export async function runFractureDetection(volumeId) {
  try {
    showToast('🦴 Перелом: анализ...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const[nx, ny, nz] = dims;
    const sliceSize = nx * ny;
  
    let suspiciousVoxels = 0;
    let totalBoneVoxels = 0;
    const suspiciousSlices = new Map(); 
  
    for (let z = 1; z < nz - 1; z++) {
      let sliceSuspicious = 0;
      for (let y = 1; y < ny - 1; y++) {
        for (let x = 1; x < nx - 1; x++) {
          const idx = z * sliceSize + y * nx + x;
          const hu = scalarData[idx];
          if (hu < 200) continue; 
          totalBoneVoxels++;
  
          const gx = scalarData[idx + 1] - scalarData[idx - 1];
          const gy = scalarData[idx + nx] - scalarData[idx - nx];
          const gz = scalarData[idx + sliceSize] - scalarData[idx - sliceSize];
          const grad = Math.sqrt(gx * gx + gy * gy + gz * gz);
  
          if (grad > 500 && hu > 300) {
            suspiciousVoxels++;
            sliceSuspicious++;
          }
        }
      }
      if (sliceSuspicious > 20) {
        suspiciousSlices.set(z, sliceSuspicious);
      }
    }
  
    const suspRate = totalBoneVoxels > 0 ? (suspiciousVoxels / totalBoneVoxels * 100).toFixed(2) : 0;
  
    const topSlices =[...suspiciousSlices.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  
    let slicesHtml = topSlices.length > 0
      ? topSlices.map(([z, cnt]) => `<tr><td style="${TD_STYLE}">Срез #${z+1}</td><td style="${TD_STYLE}">${cnt} вокселей</td></tr>`).join('')
      : `<tr><td style="${TD_STYLE}" colspan="2">Подозрительных участков не обнаружено</td></tr>`;
  
    const hasSuspicion = suspiciousSlices.size > 3;
  
    showResultModal('🦴 Детекция переломов (градиентный анализ)', `
      <div style="text-align:center; margin-bottom:15px; color:${hasSuspicion ? '#eab308' : '#22c55e'}; font-weight:600;">
        ${hasSuspicion ? '⚠️ Обнаружены подозрительные зоны — проверьте визуально' : '✅ Выраженных нарушений непрерывности не выявлено'}
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Вокселей кости</td><td style="${TD_STYLE}">${totalBoneVoxels.toLocaleString()}</td></tr>
        <tr><td style="${TD_STYLE}">Подозрительных вокселей</td><td style="${TD_STYLE}">${suspiciousVoxels.toLocaleString()} (${suspRate}%)</td></tr>
        <tr><td style="${TD_STYLE}">Подозрительных срезов</td><td style="${TD_STYLE}">${suspiciousSlices.size}</td></tr>
      </table>
      ${topSlices.length > 0 ? `
        <h4 style="color:#fff; margin:15px 0 8px;">Топ-5 подозрительных срезов:</h4>
        <table style="${TABLE_STYLE}">${slicesHtml}</table>
      ` : ''}
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Алгоритм: 3D-градиент Собеля на костных вокселях (>200 HU). Высокий градиент внутри кости = возможное нарушение целостности.<br>
        Не заменяет экспертную визуальную оценку.
      </div>
    `);
  } catch (e) { log.warn(`[clinicalTools] runFractureDetection failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  9. MedSAM — Интерактивная сегментация
// ══════════════════════════════════════════════════════════════

let _samPoints =[];
let _samActive = false;

export function activateMedSAM() {
  _samPoints =[];
  _samActive = true;
  clearSamCache();
  showToast('🎯 MedSAM активирован: Клик=позитив, Shift+Клик=негатив, Enter=сегментация', 'info');
}

export function addSAMPoint(x, y, label) {
  _samPoints.push({ x, y, label });
  showToast(`🎯 Точка ${_samPoints.length}:[${Math.round(x)},${Math.round(y)}] ${label === 1 ? '+' : '−'}`, 'info', 800);
}

export function undoSAMPoint() {
  if (_samPoints.length) {
    _samPoints.pop();
    showToast(`↩ Точек: ${_samPoints.length}`, 'info', 600);
  }
}

export function clearSAMPoints() {
  _samPoints =[];
  clearSamCache();
  showToast('🗑 SAM очищен', 'info', 600);
}

export async function runMedSAMSegmentation() {
  try {
    if (_samPoints.length === 0) {
      showToast('⚠️ Добавьте хотя бы одну точку (кликните на изображение)', 'warning');
      return;
    }
  
    const encoderOk = await isModelAvailable('medsam-encoder');
    const decoderOk = await isModelAvailable('medsam-decoder');
  
    if (!encoderOk || !decoderOk) {
      showToast('❌ ONNX-модели MedSAM не найдены в /моделей/. Проверьте vision_encoder_quantized.onnx и sam_vit_b_01ec64.decoder.onnx', 'error');
      return;
    }
  
    showToast('🤖 MedSAM: кодирование изображения...', 'info');
  
    // [FIX] Явно указываем axial — без viewportId мог вернуть данные не того среза
    const slice = getSlicePixelData('axial');
    if (!slice) { showToast('⚠️ Нет данных текущего среза', 'warning'); return; }
  
    try {
      const embedding = await samEncodeImage(
        slice.pixelData, slice.width, slice.height, `slice-${slice.sliceIdx}`
      );
      showToast('🤖 MedSAM: декодирование маски...', 'info');
  
      const result = await samDecodePoints(_samPoints, embedding);
  
      showToast(`✅ MedSAM: маска ${result.maskWidth}×${result.maskHeight}, IoU=${result.iou.toFixed(3)}`, 'success');
  
      showResultModal('🎯 MedSAM Segmentation', `
        <table style="${TABLE_STYLE}">
          <tr><td style="${TD_STYLE}">Размер маски</td><td style="${TD_STYLE}">${result.maskWidth} × ${result.maskHeight}</td></tr>
          <tr><td style="${TD_STYLE}">IOU score</td><td style="${TD_STYLE}">${result.iou.toFixed(4)}</td></tr>
          <tr><td style="${TD_STYLE}">Точек использовано</td><td style="${TD_STYLE}">${_samPoints.length}</td></tr>
          <tr><td style="${TD_STYLE}">Оригинал</td><td style="${TD_STYLE}">${result.origWidth} × ${result.origHeight}</td></tr>
          <tr><td style="${TD_STYLE}">Вокселей в маске</td><td style="${TD_STYLE}">${Array.from(result.mask).filter(v=>v>0).length.toLocaleString()}</td></tr>
        </table>
      `);
  
      return result;
  
    } catch (err) {
      showToast(`❌ MedSAM ошибка: ${err.message}`, 'error');
      log.error('[MedSAM]', err);
    }
  } catch (e) { log.warn(`[clinicalTools] runMedSAMSegmentation failed:`, e.message); return null; }
}

export function isSAMActive() { return _samActive; }
export function deactivateMedSAM() { _samActive = false; _samPoints =[]; }


// ══════════════════════════════════════════════════════════════
//  10. ДИАМЕТР АОРТЫ — автоматический на нескольких уровнях
// ══════════════════════════════════════════════════════════════

export async function runAortaDiameter(volumeId) {
  try {
    showToast('🔴 Аорта: измерение диаметров...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
    const sliceSize = nx * ny;
  
    const levels =[
      { name: 'Дуга аорты', zRatio: 0.25 },
      { name: 'Восходящая аорта', zRatio: 0.35 },
      { name: 'Нисходящая аорта', zRatio: 0.50 },
      { name: 'Брюшная аорта', zRatio: 0.75 },
    ];
  
    const results =[];
  
    for (const level of levels) {
      const z = Math.floor(nz * level.zRatio);
      const sliceStart = z * sliceSize;
  
      let sumX = 0, sumY = 0, count = 0;
      const searchX0 = Math.floor(nx * 0.3), searchX1 = Math.floor(nx * 0.7);
      const searchY0 = Math.floor(ny * 0.2), searchY1 = Math.floor(ny * 0.6);
  
      for (let y = searchY0; y < searchY1; y++) {
        for (let x = searchX0; x < searchX1; x++) {
          const hu = scalarData[sliceStart + y * nx + x];
          if ((hu > 25 && hu < 100) || (hu > 130 && hu < 500)) {
            sumX += x; sumY += y; count++;
          }
        }
      }
  
      if (count < 50) {
        results.push({ ...level, z, diameter: null });
        continue;
      }
  
      const cx = Math.round(sumX / count);
      const cy = Math.round(sumY / count);
  
      // Gap-tolerant span measurement (aorta wall/calcifications cause gaps)
      const isAortaHU = (hu) => hu > 15 && hu < 600;
      const GAP = 5;
      
      let xR = 0, gR = 0;
      for (let x = cx; x < Math.min(cx + 80, nx); x++) {
        if (isAortaHU(scalarData[sliceStart + cy * nx + x])) { xR++; gR = 0; }
        else { gR++; if (gR > GAP) break; }
      }
      let xL = 0, gL = 0;
      for (let x = cx - 1; x >= Math.max(cx - 80, 0); x--) {
        if (isAortaHU(scalarData[sliceStart + cy * nx + x])) { xL++; gL = 0; }
        else { gL++; if (gL > GAP) break; }
      }
      let yD = 0, gD = 0;
      for (let y = cy; y < Math.min(cy + 80, ny); y++) {
        if (isAortaHU(scalarData[sliceStart + y * nx + cx])) { yD++; gD = 0; }
        else { gD++; if (gD > GAP) break; }
      }
      let yU = 0, gU = 0;
      for (let y = cy - 1; y >= Math.max(cy - 80, 0); y--) {
        if (isAortaHU(scalarData[sliceStart + y * nx + cx])) { yU++; gU = 0; }
        else { gU++; if (gU > GAP) break; }
      }
      const xSpan = xR + xL, ySpan = yD + yU;
  
      const diamX = xSpan * spacing[0];
      const diamY = ySpan * spacing[1];
      const avgDiam = ((diamX + diamY) / 2).toFixed(1);
  
      results.push({ ...level, z, diameter: avgDiam, diamX: diamX.toFixed(1), diamY: diamY.toFixed(1) });
    }
  
    let rowsHtml = results.map(r => `
      <tr>
        <td style="${TD_STYLE}">${r.name}</td>
        <td style="${TD_STYLE}">Срез #${r.z + 1}</td>
        <td style="${TD_STYLE}; font-weight:600; color:${r.diameter && parseFloat(r.diameter) > 40 ? '#ef4444' : '#22c55e'};">
          ${r.diameter ? `${r.diameter} мм` : '—'}
        </td>
      </tr>
    `).join('');
  
    showResultModal('🔴 Диаметры аорты', `
      <table style="${TABLE_STYLE}">
        <tr>
          <th style="${TH_STYLE}">Уровень</th>
          <th style="${TH_STYLE}">Срез</th>
          <th style="${TH_STYLE}">Диаметр</th>
        </tr>
        ${rowsHtml}
      </table>
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Автоматическое определение. Норма: восходящая ≤40 мм, нисходящая ≤30 мм.<br>
        >45 мм = аневризма. Верифицируйте вручную.
      </div>
    `);
  } catch (e) { log.warn(`[clinicalTools] runAortaDiameter failed:`, e.message); showToast('❌ Аорта: ' + e.message, 'error', 3000); }
}


// ══════════════════════════════════════════════════════════════
//  EXPORT: привязка кнопок (вызывается из uiManager.js)
// ══════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════
// 3D LABELMAP -> MPR Labelmap overlay (CS3D v2 API)
// ══════════════════════════════════════════════════════════════
async function applyLabelMapToCornerstone(labelMap, volumeId, options = {}) {
  // [10.19.24] Переключатель «Авто-наложение» (панель ИИ) читается ЗДЕСЬ —
  // в единственной точке, где маска ложится на срезы. До 10.19.24 галочка была
  // мертва (это было прямо записано в enterprisePanel.js); теперь «выкл»
  // означает: числа посчитаны и показаны, а картинка не тронута.
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('mikhmel.aiAutoOverlay') === 'off') {
      showToast('Наложение маски отключено переключателем «Авто-наложение» (панель ИИ) — числа посчитаны, картинка не тронута', 'info', 5000);
      return { пропущено: true, причина: 'авто-наложение выключено' };
    }
  } catch (e) { /* хранилище закрыто — накладываем как раньше */ }
  const cs = cornerstone;
  const { segmentationId = 'lungSeg3D' } = options;

  const srcVid = volumeId.startsWith('cornerstoneStreamingImageVolume:')
    ? volumeId : 'cornerstoneStreamingImageVolume:' + volumeId;
  const srcVolume = cs.cache.getVolume(srcVid);
  if (!srcVolume) throw new Error('Source volume not found in cache');

  const csTools = await import('@cornerstonejs/tools');
  const { segmentation: segAPI, Enums: toolEnums } = csTools;

  const segVolumeId = segmentationId;

  // Чистим старое
  try { cs.cache.removeVolumeLoadObject(segVolumeId); } catch (_) { log.debug('[ClinicalTools]', _.message); }
  try { cs.cache.removeVolumeLoadObject('cornerstoneStreamingImageVolume:' + segVolumeId); } catch (_) { log.debug('[ClinicalTools]', _.message); }
  try { segAPI.state.removeSegmentation(segmentationId); } catch (_) { log.debug('[ClinicalTools]', _.message); }

  // Derived volume с тем же grid
  log.debug('[Seg3D] Creating derived labelmap volume...');
  const segVolume = await cs.volumeLoader.createAndCacheDerivedLabelmapVolume(srcVid, {
    volumeId: segVolumeId,
  });

  // Заполняем scalarData
  let segSD;
  if (segVolume.voxelManager) {
    segSD = segVolume.voxelManager.getCompleteScalarDataArray();
  } else if (segVolume.getScalarData) {
    segSD = segVolume.getScalarData();
  } else if (segVolume.scalarData) {
    segSD = segVolume.scalarData;
  }

  if (!segSD) throw new Error('Cannot get scalarData from segVolume');

  const len = Math.min(segSD.length, labelMap.length);
  for (let i = 0; i < len; i++) segSD[i] = labelMap[i];
  log.debug(`[Seg3D] Filled ${len} voxels, non-zero: ${labelMap.filter(v => v > 0).length}`);

  if (segVolume.imageData) segVolume.imageData.modified();

  // Регистрация сегментации в state
  segAPI.addSegmentations([{
    segmentationId,
    representation: {
      type: toolEnums.SegmentationRepresentations.Labelmap,
      data: { volumeId: segVolumeId },
    },
  }]);
  log.debug('[Seg3D] Segmentation added to state');

  // Собираем MPR viewports
  const re = getRenderingEngine();
  const vpIds = getViewportIds();
  const viewportInputMap = {};

  for (const vpId of vpIds) {
    const vp = re.getViewport(vpId);
    if (!vp) continue;
    const is3D = vpId.includes('3d') || vpId.includes('volume') || vp.type === 'volume3d';
    if (is3D) continue;
    viewportInputMap[vpId] = [{
      segmentationId,
    }];
  }

  const mprVpIds = Object.keys(viewportInputMap);
  log.debug(`[Seg3D] Adding labelmap to ${mprVpIds.length} MPR viewports: ${mprVpIds.join(', ')}`);

  if (mprVpIds.length > 0) {
    // Пробуем v2 API (addLabelmapRepresentationToViewportMap)
    if (typeof segAPI.addLabelmapRepresentationToViewportMap === 'function') {
      await segAPI.addLabelmapRepresentationToViewportMap(viewportInputMap);
      log.debug('[Seg3D] v2 addLabelmapRepresentationToViewportMap OK');
    } else if (typeof segAPI.addSegmentationRepresentations === 'function') {
      // Fallback: старый API
      for (const vpId of mprVpIds) {
        await segAPI.addSegmentationRepresentations(vpId, [{
          segmentationId,
          type: toolEnums.SegmentationRepresentations.Labelmap,
        }]);
      }
      log.debug('[Seg3D] legacy addSegmentationRepresentations OK');
    } else {
      log.error('[Seg3D] No suitable API found for adding representations');
    }
  }

  re.render();
  log.debug('[Seg3D] MPR overlays done');
  return { segVolumeId, segmentationId };
}


/**
 * Bind click handlers to all clinical tool buttons.
 * This is the SINGLE source of button bindings — no stubs in uiManager.
 * @param {function():string} getVolumeId - Returns current volume ID
 */
export function bindAllClinicalTools(getVolumeId) {
  const $ = (id) => document.getElementById(id);
  const on = (id, evt, fn) => { const el = $(id); if (el) el.addEventListener(evt, fn, { signal: _ac.signal }); };

  // ─── Algorithmic tools (не требуют ONNX моделей) ──────────
  on('btn-agatston', 'click', () => runAgatstonScore(getVolumeId()));
  on('btn-emphysema', 'click', () => runEmphysemaQuantification(getVolumeId()));
  on('btn-ctr', 'click', () => runCTR(getVolumeId()));
  on('btn-bmd', 'click', () => runBMD(getVolumeId()));
  on('btn-pneumothorax', 'click', () => runPneumothoraxDetection(getVolumeId()));
  on('btn-fracture-detect', 'click', () => runFractureDetection(getVolumeId()));
  on('btn-aorta-diameter', 'click', () => runAortaDiameter(getVolumeId()));

  on('btn-region-grow', 'click', () => {
    const range = parseInt($(('region-grow-range'))?.value || '100');
    runRegionGrow3D(getVolumeId(), { range });
  });

  // ─── Cardiac ──────────────────────────────────────────────
  // [10.19.32] Сирота btn-cardiac-seg удалён: кнопки нет в разметке. Живой
  // вход один — карточка «Камеры сердца» вкладки ИИ (словарь data-model:
  // 'cardiac' → runCardiacSegmentation ниже).

  // ─── MedSAM (ONNX) ───────────────────────────────────────
  on('btn-medsam', 'click', () => activateMedSAM());
  on('btn-sam-undo', 'click', () => undoSAMPoint());
  on('btn-sam-clear', 'click', () => clearSAMPoints());
  on('btn-sam-run', 'click', () => runMedSAMSegmentation());

  // [11.1.29] Значки движка карточкам с data-model — ПО ФАКТУ (аудит §2.1):
  // hasAlgorithmicFallback решает, кто считает; разметка знать этого не
  // может — набор откатов живёт в aiModule.
  document.querySelectorAll('.ai-run-btn[data-model]').forEach((btn) => {
    const шапка = btn.closest('.ai-card')?.querySelector('.ai-card-header');
    if (!шапка || шапка.querySelector('.ai-engine')) return;
    const алг = hasAlgorithmicFallback(btn.dataset.model);
    const з = document.createElement('span');
    з.className = 'ai-engine';
    з.dataset.eng = алг ? 'algo' : 'net';
    з.textContent = алг ? 'алгоритм' : 'нейросеть';
    з.title = алг
      ? 'Считает встроенный алгоритм по плотности — файл нейросети не используется, даже если лежит в /моделей/'
      : 'Считает нейросеть (ONNX): нужен файл модели в /моделей/';
    шапка.insertBefore(з, шапка.querySelector('.ai-card-type'));
  });

  // ─── AI model buttons (.ai-run-btn) ──────────────────────
  document.querySelectorAll('.ai-run-btn[data-model]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const modelId = e.currentTarget.dataset.model;
      const card = e.currentTarget.closest('.ai-card');
      const bar = card?.querySelector('.ai-progress-bar');

      log.debug(`\n=========================================`);
      log.debug(`[ClinicalTools] 🖱 Клик по кнопке ИИ: ${modelId}`);

      if (hasAlgorithmicFallback(modelId)) {
        log.debug(`[ClinicalTools] ⏭ Модель ${modelId} имеет алгоритмический fallback. Игнорируем ONNX.`);
        return;
      }

      log.debug(`[ClinicalTools] ⏳ Проверка доступности файла модели...`);
      const available = await isModelAvailable(modelId);
      if (!available) {
        log.error(`[ClinicalTools] 🛑 ОСТАНОВКА: Модель ${modelId} не найдена на сервере! Проверьте папку /public/models/`);
        showToast(`❌ Модель ${modelId} не найдена в /моделей/`, 'error');
        return;
      }

      showToast(`🤖 Нейросеть ${modelId}: загрузка...`, 'info');   // [11.1.29] честно называем счётчика
      if (bar) bar.style.width = '20%';

      try {
        log.debug(`[ClinicalTools] 💾 Запрос данных КТ объема...`);
        const vd = getVolumeData(getVolumeId());
        if (!vd) { 
          log.error(`[ClinicalTools] 🛑 ОСТАНОВКА: Нет данных объема КТ. getVolumeData вернул null.`);
          showToast('⚠️ Загрузите КТ объём', 'warning'); 
          return; 
        }

        log.debug(`[ClinicalTools] ✅ Данные получены! Исходный размер: ${vd.dims.join(' x ')}`);

        if (modelId.includes('lungmask') || modelId.includes('lung-seg')) {
          log.debug('[ClinicalTools] \u{1F9E0} 3D lung segmentation start');
          showToast('\u{1FAC1} 3D-сегментация: загружаю модель...', 'info');
          if (bar) bar.style.width = '20%';

          await loadModel(modelId);
          if (bar) bar.style.width = '30%';

          const { scalarData, dims, spacing } = vd;
          const [nx, ny, nz] = dims;

          log.debug(`[ClinicalTools] \u{1F4CA} Starting 3D loop: ${nz} slices (all)`);
          showToast(`\u{1FAC1} Обработка ${nz} срезов (все)...`, 'info');

          const labelMap = await runSegmentation3DLoop(scalarData, dims, modelId, {
            targetDim: 256,
            numClasses: 3,
            huMin: -1024,
            huMax: 600,
            batchSkip: 0,
            onProgress: (pct, z) => {
              if (bar) bar.style.width = `${30 + pct * 0.6}%`;
              if (pct % 20 === 0) showToast(`\u{1FAC1} ${pct}% (срез ${z+1}/${nz})`, 'info');
            },
          });

          log.debug('[ClinicalTools] \u2705 3D loop done, applying overlay...');
          if (bar) bar.style.width = '92%';

          // Stats
          let rightCount = 0, leftCount = 0;
          for (let i = 0; i < labelMap.length; i++) {
            if (labelMap[i] === 1) rightCount++;
            else if (labelMap[i] === 2) leftCount++;
          }
          const voxelVol = spacing[0] * spacing[1] * spacing[2];
          const rightVolMl = (rightCount * voxelVol / 1000).toFixed(0);
          const leftVolMl = (leftCount * voxelVol / 1000).toFixed(0);
          const totalVolMl = ((rightCount + leftCount) * voxelVol / 1000).toFixed(0);

          // Накладываем маску на 2D срезы (твоя текущая рабочая логика)
          try {
            await applyLabelMapToCornerstone(labelMap, getVolumeId(), {
              segmentationId: 'lungSeg3D',
            });
          } catch (overlayErr) {
            log.error('[ClinicalTools] Overlay error:', overlayErr);
          }

          if (bar) bar.style.width = '100%';

          // === ЧИСТЫЙ 3D РЕНДЕР ЛЕГКИХ (БЕЗ ТАБЛИЦ) ===
          try {
            log.debug('[ClinicalTools] Генерация 3D модели легких (Marching Cubes)...');
            showToast('Построение 3D поверхностей...', 'info');
            
            const re = getRenderingEngine();
            const vpIds = getViewportIds();
            const vp3dId = vpIds.find(id => id.includes('3d') || id.includes('volume') || id === 'volume3d');
            
            if (vp3dId) {
               const vp3d = re.getViewport(vp3dId);
               const vtkRenderer = vp3d.getRenderer(); 
               
               if (vtkRenderer) {
                 // Загружаем не только кубы и актеры, но и базовые классы данных VTK
                 const [
                   { default: vtkImageMarchingCubes },
                   { default: vtkMapper },
                   { default: vtkActor },
                   { default: vtkImageData },
                   { default: vtkDataArray }
                 ] = await Promise.all([
                   import('@kitware/vtk.js/Filters/General/ImageMarchingCubes'),
                   import('@kitware/vtk.js/Rendering/Core/Mapper'),
                   import('@kitware/vtk.js/Rendering/Core/Actor'),
                   import('@kitware/vtk.js/Common/DataModel/ImageData'),
                   import('@kitware/vtk.js/Common/Core/DataArray')
                 ]);

                 // 1. Создаем правильный VTK-объект из сырого массива, чтобы ничего не падало
                 const customVtkImage = vtkImageData.newInstance();
                 customVtkImage.setDimensions(dims);
                 customVtkImage.setSpacing(spacing);
                 if (vd.origin) customVtkImage.setOrigin(vd.origin);
                 if (vd.imageData && vd.imageData.getDirection) {
                     customVtkImage.setDirection(vd.imageData.getDirection()); // правильный поворот
                 }

                 // Привязываем наш массив labelMap
                 const scalars = vtkDataArray.newInstance({
                     name: 'Scalars',
                     values: labelMap, 
                     numberOfComponents: 1,
                 });
                 customVtkImage.getPointData().setScalars(scalars);

                 // 2. Правое легкое (Label 1)
                 const mcRight = vtkImageMarchingCubes.newInstance({ contourValue: 1, computeNormals: true });
                 mcRight.setInputData(customVtkImage);
                 const mapperRight = vtkMapper.newInstance();
                 mapperRight.setInputConnection(mcRight.getOutputPort());
                 const actorRight = vtkActor.newInstance();
                 actorRight.setMapper(mapperRight);
                 actorRight.getProperty().setOpacity(0.5); 
                 actorRight.getProperty().setColor(0.3, 0.8, 0.3); // Зеленый цвет
                 vtkRenderer.addActor(actorRight);

                 // 3. Левое легкое (Label 2)
                 const mcLeft = vtkImageMarchingCubes.newInstance({ contourValue: 2, computeNormals: true });
                 mcLeft.setInputData(customVtkImage);
                 const mapperLeft = vtkMapper.newInstance();
                 mapperLeft.setInputConnection(mcLeft.getOutputPort());
                 const actorLeft = vtkActor.newInstance();
                 actorLeft.setMapper(mapperLeft);
                 actorLeft.getProperty().setOpacity(0.5); 
                 actorLeft.getProperty().setColor(0.3, 0.5, 0.8); // Синий цвет
                 vtkRenderer.addActor(actorLeft);
                 vtkRenderer.resetCamera(); 
                 
                 

                 // Обновляем 3D окно
                 vp3d.render();
                 log.debug('[ClinicalTools] 3D модель успешно отрендерена');
                 showToast('✅ 3D легкие добавлены', 'success');
               }
            }
          } catch (e) {
             log.error('[ClinicalTools] Ошибка 3D рендера:', e);
             showToast('Ошибка при построении 3D', 'error');
          }
          // ==============================================


          showToast(`\u2705 Лёгкие: ${totalVolMl} мл (R:${rightVolMl} L:${leftVolMl})`, 'success');

        } else {
          log.debug(`[ClinicalTools] Запуск стандартной модели ${modelId}`);
          await loadModel(modelId);
          if (bar) bar.style.width = '100%';
          showToast(`✅ Модель ${modelId} загружена`, 'success');
        }

      } catch (err) {
        showToast(`❌ Ошибка выполнения ИИ: ${err.message}`, 'error');
        log.error('\n[ClinicalTools] ❌ КРИТИЧЕСКАЯ ОШИБКА ИИ:', err);
      }
    }, { signal: _ac.signal });
  });

  // [10.19.28] Пять незаконченных кнопок УДАЛЕНЫ по решению владельца, а не
  // помечены: «Маркировка коронаров», «Смещение средней линии», «Нумерация
  // позвонков», «Трассировка сосудов», «Калибровка линейки». В 10.19.9 их
  // пометили заглушками, чтобы они перестали врать, — но кнопка, которая честно
  // говорит «не реализовано», всё равно занимает место и обещает продолжение.
  // Здесь стоял их список с текстами отказов; вместе с кнопками ушёл и он.
  //
  // Что осталось вместо них, слово в слово из тех же пояснений: кальциевый счёт
  // считается по-настоящему, но без разбивки по LAD/LCx/RCA; смещение средней
  // линии измеряется «Линейкой» — она калибрована по DICOM и даёт миллиметры;
  // BMD по Pickhardt работает, уровень позвонка врач указывает сам; диаметр
  // аорты измеряется; размер пикселя берётся из DICOM, поэтому ручная
  // калибровка не нужна (и опасна: неверная молча испортит ВСЕ измерения).

  log.debug('[ClinicalTools] ✅ Все инструменты привязаны');
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Batch analysis, report generator, history
// ══════════════════════════════════════════════════════════════

/** @type {Array<{tool:string, result:Object, timestamp:number}>} */
const _clinicalHistory = [];
const CLINICAL_HISTORY_MAX = 50;

/**
 * Get clinical analysis history
 * @returns {Array}
 */
export function getClinicalHistory() { return [..._clinicalHistory]; }

/**
 * Clear clinical history
 */
export function clearClinicalHistory() { _clinicalHistory.length = 0; }

/**
 * Run batch analysis — multiple clinical tools at once
 * @param {string} volumeId
 * @param {string[]} tools — e.g. ['agatston', 'ctr', 'emphysema']
 * @returns {Promise<Object>} results keyed by tool name
 */
export async function runBatchAnalysis(volumeId, tools = []) {
  try {
    const results = {};
    const toolMap = {
      'agatston': runAgatstonScore,
      'emphysema': runEmphysemaQuantification,
      'ctr': runCTR,
      'bmd': runBMD,
      'pneumothorax': runPneumothoraxDetection,
      'fracture': runFractureDetection,
      'aorta': runAortaDiameter,
      'cardiac': runCardiacSegmentation,
    };
    for (const t of tools) {
      const fn = toolMap[t];
      if (!fn) { results[t] = { error: 'Unknown tool' }; continue; }
      try {
        results[t] = await fn(volumeId);
        _clinicalHistory.push({ tool: t, result: results[t], timestamp: Date.now() });
        if (_clinicalHistory.length > CLINICAL_HISTORY_MAX) _clinicalHistory.shift();
      } catch (e) { results[t] = { error: e.message }; }
    }
    return results;
  } catch (e) { log.warn(`[clinicalTools] runBatchAnalysis failed:`, e.message); return {}; }
}

/**
 * Export clinical history as CSV
 */
export function exportClinicalHistoryCSV() {
  if (!_clinicalHistory.length) return;
  const rows = [['Tool', 'Timestamp', 'Result Summary']];
  for (const h of _clinicalHistory) {
    const summary = JSON.stringify(h.result).substring(0, 200);
    rows.push([h.tool, new Date(h.timestamp).toISOString(), summary]);
  }
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `clinical_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Get list of all available clinical tools
 * @returns {string[]}
 */
export function listAvailableClinicalTools() {
  return ['agatston', 'emphysema', 'ctr', 'bmd', 'pneumothorax', 'fracture', 'aorta', 'cardiac', 'regionGrow', 'medSAM'];
}

// [FIX] Cleanup all event listeners
export function destroyClinicalTools() {
  _ac.abort();
  _ac = new AbortController();
}
